import { type ComponentProps, type ReactNode } from 'react'

import {
  Divider,
  Field,
  Flex,
  TextButton,
  UncontrolledDateRangePicker,
} from '@procurify/ui'

import { type FilterManagerContextValues } from '@webapp/components/FilterManager/FilterManagerContext'
import { FiltersPopover } from '@webapp/components/FiltersPopover'
import { RequesterSelect } from '@webapp/components/RequesterSelect'
import { SavedFilterManager } from '@webapp/components/SavedFilterManager'
import { PrimaryCategorySelect } from '@webapp/core/components/PrimaryCategorySelect'
import { SecondaryCategorySelect } from '@webapp/core/components/SecondaryCategorySelect'
import { SecondaryCategory } from '@webapp/core/entities/internal/SecondaryCategory'
import { formatYYYYMMDD } from '@webapp/core/utils/dates/formatYYYYMMDD/formatYYYYMMDD'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ApprovedExpenseItem } from '@webapp/modules/expense/entities/ApprovedExpenseItem'
import messages from './messages'
import {
  type ApprovedExpenseItemListFilterValues,
  type RequesterFilterReference,
  useApprovedExpenseItemListPresenter,
} from './useApprovedExpenseItemListPresenter'
import { ApprovedExpenseItemTable } from '../../views/ApprovedExpenseItemTable'

const SAVED_FILTER_COLLECTION = 'approved-requests.expense'

type DateFilterKey =
  | 'order_created_date_0'
  | 'order_created_date_1'
  | 'approved_datetime_0'
  | 'approved_datetime_1'

interface IApprovedExpenseItemListFiltersProps {
  filterManager: FilterManagerContextValues<ApprovedExpenseItemListFilterValues>
}

/**
 * Filters popover for the approved expense items list: saved filters
 * (collection approved-requests.expense), PrimaryCategory → SecondaryCategory
 * cascade (BHV-007), requester, and submitted/approved date ranges. Filter
 * state persists to the URL via the FilterManager (BHV-008).
 */
const ApprovedExpenseItemListFilters = ({
  filterManager,
}: IApprovedExpenseItemListFiltersProps) => {
  const intl = useProcurifyIntl()

  const { addFilter, removeFilter, clearFilters, filterValues } = filterManager

  const isFiltered =
    Object.values(filterValues).filter((value) => !!value).length > 0

  const handlePrimaryCategoryChange = (
    value: Nullable<ApprovedExpenseItemListFilterValues['location']>
  ) => {
    // PrimaryCategory change clears the SecondaryCategory selection and
    // re-scopes its options (BHV-007)
    if (filterValues.department) {
      removeFilter({
        key: 'department',
        value: filterValues.department,
        paramValue: filterValues.department.id,
      })
    }

    if (value) {
      addFilter({ key: 'location', value, paramValue: value.id })
    }
  }

  const handleSecondaryCategoryChange = (
    value: Nullable<ApprovedExpenseItemListFilterValues['department']>
  ) => {
    if (value) {
      addFilter({ key: 'department', value, paramValue: value.id })
    }
  }

  const handleRequesterChange = (value: Nullable<RequesterFilterReference>) => {
    if (value) {
      addFilter({
        key: 'requester',
        value: {
          id: value.id,
          firstName: value.firstName,
          lastName: value.lastName,
        },
        paramValue: value.id,
      })
    }
  }

  const handleDateRangeChange = (
    startKey: DateFilterKey,
    endKey: DateFilterKey,
    range: { startDate: Date; endDate: Date }
  ) => {
    const start = formatYYYYMMDD(range.startDate)
    const end = formatYYYYMMDD(range.endDate)

    addFilter({ key: startKey, value: start, paramValue: start })
    addFilter({ key: endKey, value: end, paramValue: end })
  }

  const toDate = (value?: string) => (value ? new Date(value) : undefined)

  const decodeDateFilter = (key: DateFilterKey) => (savedValue: string) => ({
    key,
    value: savedValue,
    paramValue: savedValue,
  })

  return (
    <SavedFilterManager
      collectionId={SAVED_FILTER_COLLECTION}
      filterManager={filterManager}
      decodeFilters={{
        location: (savedValue) => ({
          key: 'location',
          value: { id: Number(savedValue.id), name: savedValue.name },
          paramValue: savedValue.id,
        }),
        department: (savedValue) => ({
          key: 'department',
          value: new SecondaryCategory({
            id: Number(savedValue.id),
            name: savedValue.name,
            primaryCategory: null,
          }),
          paramValue: savedValue.id,
        }),
        requester: (savedValue) => ({
          key: 'requester',
          value: savedValue,
          paramValue: savedValue.id,
        }),
        order_created_date_0: decodeDateFilter('order_created_date_0'),
        order_created_date_1: decodeDateFilter('order_created_date_1'),
        approved_datetime_0: decodeDateFilter('approved_datetime_0'),
        approved_datetime_1: decodeDateFilter('approved_datetime_1'),
      }}
      encodeFilters={{
        location: (value) => ({ id: value.id, name: value.name }),
        department: (value) => ({ id: value.id, name: value.name }),
        requester: (value) => ({
          id: value.id,
          firstName: value.firstName,
          lastName: value.lastName,
        }),
        order_created_date_0: (value) => value,
        order_created_date_1: (value) => value,
        approved_datetime_0: (value) => value,
        approved_datetime_1: (value) => value,
      }}
    >
      <FiltersPopover>
        {({ dismiss }) => (
          <>
            <Flex.Row width={480}>
              <Flex.Item width={1}>
                <Field
                  label={intl.formatMessage(messages.savedFiltersLabel)}
                  labelFor='approved-expense-items-saved-filter'
                >
                  <SavedFilterManager.Select
                    inputId='approved-expense-items-saved-filter'
                    onEdit={dismiss}
                  />
                </Field>
              </Flex.Item>
            </Flex.Row>
            <Divider />
            <Flex.Row width={480}>
              <Flex.Item width={1 / 2}>
                <Field
                  label={intl.formatMessage(
                    messages.primaryCategoryFilterLabel
                  )}
                  labelFor='approved-expense-items-primary-category-filter'
                >
                  <PrimaryCategorySelect
                    inputId='approved-expense-items-primary-category-filter'
                    value={filterValues.location ?? null}
                    onChange={handlePrimaryCategoryChange}
                  />
                </Field>
              </Flex.Item>
              <Flex.Item width={1 / 2}>
                <Field
                  label={intl.formatMessage(
                    messages.secondaryCategoryFilterLabel
                  )}
                  labelFor='approved-expense-items-secondary-category-filter'
                >
                  <SecondaryCategorySelect
                    inputId='approved-expense-items-secondary-category-filter'
                    key={filterValues.location?.id}
                    primaryCategoryId={filterValues.location?.id}
                    value={filterValues.department ?? null}
                    onChange={handleSecondaryCategoryChange}
                  />
                </Field>
              </Flex.Item>
            </Flex.Row>
            <Flex.Row width={480}>
              <Flex.Item width={1 / 2}>
                <Field
                  label={intl.formatMessage(
                    messages.submittedDateRangeFilterLabel
                  )}
                >
                  <UncontrolledDateRangePicker
                    name='approved-expense-items-submitted-date-range'
                    startDate={toDate(filterValues.order_created_date_0)}
                    endDate={toDate(filterValues.order_created_date_1)}
                    onChange={(range) =>
                      handleDateRangeChange(
                        'order_created_date_0',
                        'order_created_date_1',
                        range
                      )
                    }
                  />
                </Field>
              </Flex.Item>
              <Flex.Item width={1 / 2}>
                <Field
                  label={intl.formatMessage(
                    messages.approvedDateRangeFilterLabel
                  )}
                >
                  <UncontrolledDateRangePicker
                    name='approved-expense-items-approved-date-range'
                    startDate={toDate(filterValues.approved_datetime_0)}
                    endDate={toDate(filterValues.approved_datetime_1)}
                    onChange={(range) =>
                      handleDateRangeChange(
                        'approved_datetime_0',
                        'approved_datetime_1',
                        range
                      )
                    }
                  />
                </Field>
              </Flex.Item>
            </Flex.Row>
            <Flex.Row width={480}>
              <Flex.Item width={1 / 2}>
                <Field
                  label={intl.formatMessage(messages.requesterFilterLabel)}
                  labelFor='approved-expense-items-requester-filter'
                >
                  <RequesterSelect
                    inputId='approved-expense-items-requester-filter'
                    value={
                      (filterValues.requester ??
                        null) as unknown as ComponentProps<
                        typeof RequesterSelect
                      >['value']
                    }
                    onChange={handleRequesterChange}
                  />
                </Field>
              </Flex.Item>
            </Flex.Row>
            <Divider />
            <Flex.Row>
              <Flex.Item>
                <SavedFilterManager.SaveButton onClick={dismiss} />
              </Flex.Item>
              <Flex.Item ml='auto'>
                <TextButton
                  onClick={() => {
                    clearFilters()
                    dismiss()
                  }}
                  disabled={!isFiltered}
                >
                  {intl.formatMessage(messages.clearFiltersButton)}
                </TextButton>
              </Flex.Item>
            </Flex.Row>
          </>
        )}
      </FiltersPopover>
    </SavedFilterManager>
  )
}

export interface IApprovedExpenseItemListPresenterProps {
  onEditItem: (item: ApprovedExpenseItem) => void
  renderAccountCell: (item: ApprovedExpenseItem) => ReactNode
}

export const ApprovedExpenseItemListPresenter = ({
  onEditItem,
  renderAccountCell,
}: IApprovedExpenseItemListPresenterProps) => {
  const {
    data: { items },
    states: { isFetching },
    methods: {
      buildExpenseDetailUrl,
      resolveAttachmentUrl,
      resolveCurrencyCode,
    },
    filterManager,
  } = useApprovedExpenseItemListPresenter()

  return (
    <ApprovedExpenseItemTable
      items={items}
      isLoading={isFetching}
      onEditItem={onEditItem}
      renderAccountCell={renderAccountCell}
      buildExpenseDetailUrl={buildExpenseDetailUrl}
      resolveAttachmentUrl={resolveAttachmentUrl}
      resolveCurrencyCode={resolveCurrencyCode}
      filtersSlot={
        <ApprovedExpenseItemListFilters filterManager={filterManager} />
      }
    />
  )
}

ApprovedExpenseItemListPresenter.displayName =
  'ApprovedExpenseItemListPresenter'
